import { addMonths, format, isAfter, startOfMonth } from 'date-fns';
import { getBusinessDateString } from '@/utils/dateHelpers';
import type { RevenueReportLoan } from '@/utils/banker-recon/revenueReportProjection';
import { hasClearingActivity, type WireDecompositionItem } from '@/utils/banker-recon/wireDecomposition';
import type { BrokeredRevenueRecord, ServicingCashEvent } from '@/types/companyRevenueReport';

export interface CompanyRevenueReportFilters {
  loanOfficer: string;
  investor: string;
  searchQuery: string;
}

export interface AccountingTotals {
  wireReceived: number;
  revenue: number;
  expenses: number;
  netProfit: number;
  loansWithPassThrough: number;
  auditWarnings: number;
}

export interface AuditTotals {
  revenueInWire: number;
  revenueOutside: number;
  revenue: number;
  expenseInWire: number;
  expenseOutside: number;
  expenses: number;
  passThrough: number;
  passThroughOutside: number;
  netProfit: number;
}

export interface CompanyRevenueDisplayTotals {
  wire: number;
  loCompensation: number;
  netWire: number;
  revenue: number;
  expenses: number;
  passThrough: number;
  passThroughOutside: number;
  netProfit: number;
  revenueInWire: number;
  expenseInWire: number;
  expenseOutside: number;
  volume: number;
  units: number;
}

export type WireDeductionDisposition = 'deduction' | 'credit' | 'zero';

/** Classify the displayed sign after symmetric cent rounding. */
export function classifyWireDeduction(amount: number): WireDeductionDisposition {
  const absoluteCents = Math.round(Math.abs(amount) * 100);
  if (absoluteCents === 0) {
    return 'zero';
  }

  return amount < 0 ? 'credit' : 'deduction';
}

export interface UnifiedCompanyRevenueTotals {
  bankedResidualWires: number;
  bankedContributionBeforeServicing: number;
  purchaseBasisServicingInterest: number;
  brokeredGrossChecks: number;
  brokeredCompanyRevenue: number;
  servicingCashReceived: number;
  servicingInterestIncome: number;
  servicingPrincipalReceived: number;
  servicingEscrowReceived: number;
  consolidatedCompanyIncome: number;
  bankedUnits: number;
  brokeredUnits: number;
  servicingEvents: number;
}

export interface RevenueMonthBucket {
  key: string;      // 'yyyy-MM'
  label: string;    // 'MMM yyyy'
  revenue: number;
  expenses: number;
  netProfit: number;
  volume: number;   // Σ loanAmount
  units: number;
  avgLoanSize: number;
  avgRevenuePerLoan: number;
  avgNetProfitPerLoan: number;
  momNetProfitDelta: number | null;
  momNetProfitPct: number | null;
}

export interface UnifiedRevenueMonthBucket {
  key: string;
  label: string;
  bankedContribution: number;
  brokeredCompanyRevenue: number;
  servicingIncome: number;
  totalCompanyIncome: number;
  units: number;
}

export interface AccountingExportRow {
  loanNumber: string;
  borrowerName: string;
  loanOfficer: string;
  investorName: string;
  investorPurchaseDate: string | null;
  loanAmount: number;
  wireReceived: number;
  computedWire: number;
  wireDifference: number;
  revenue: number;
  expenses: number;
  netProfit: number;
  clearingWireEffect: number;
  shortageFundedGrossMemo: number;
  shortageFundedWireEffect: number;
  shortageRecoupedGrossMemo: number;
  shortageRecoupedWireEffect: number;
  netShortageGrossMemo: number;
  netShortageWireEffect: number;
  escrowClearingWireEffect: number;
  nonQmHaircutReserveWireEffect: number;
  governmentFundingGrossMemo: number;
  governmentFundingWireEffect: number;
  buydownPassThroughGrossMemo: number;
  buydownPassThroughWireEffect: number;
  reimbursableClearingWireEffect: number;
  servicingPrincipalSettlementWireEffect: number;
  otherClearingWireEffect: number;
  fhaUfmipPremiumGrossMemo: number;
  fhaUfmipPremiumWireEffect: number;
  fhaUfmipHudRemittance: number;
  fhaUfmipOutsideClearingEffect: number;
  fhaUfmipCompanyVariance: number;
  balanced: string;
  suggestedTreatment: string;
}

export interface ClearingActivitySummary {
  shortageFundedGrossMemo: number;
  shortageFundedWireEffect: number;
  shortageRecoupedGrossMemo: number;
  shortageRecoupedWireEffect: number;
  netShortageGrossMemo: number;
  netShortageWireEffect: number;
  escrowWireEffect: number;
  nonQmHaircutReserveWireEffect: number;
  governmentFundingGrossMemo: number;
  governmentFundingWireEffect: number;
  buydownPassThroughGrossMemo: number;
  buydownPassThroughWireEffect: number;
  reimbursableWireEffect: number;
  servicingPrincipalSettlementWireEffect: number;
  otherClearingWireEffect: number;
  fhaUfmipPremiumGrossMemo: number;
  fhaUfmipPremiumWireEffect: number;
  fhaUfmipHudRemittance: number;
  fhaUfmipOutsideClearingEffect: number;
  totalClearingWireEffect: number;
  totalOutsideClearingEffect: number;
}

export function filterRevenueReportLoans(
  loans: RevenueReportLoan[],
  filters: CompanyRevenueReportFilters
): RevenueReportLoan[] {
  const search = filters.searchQuery.trim().toLowerCase();

  return loans.filter((loan) => {
    if (filters.loanOfficer !== 'all' && loan.loName !== filters.loanOfficer) {
      return false;
    }

    if (filters.investor !== 'all' && loan.investorName !== filters.investor) {
      return false;
    }

    if (!search) {
      return true;
    }

    return loan.loanNumber.toLowerCase().includes(search)
      || loan.borrowerName.toLowerCase().includes(search);
  });
}

export function buildAccountingTotals(loans: RevenueReportLoan[]): AccountingTotals {
  return loans.reduce<AccountingTotals>((totals, loan) => ({
    wireReceived: totals.wireReceived + loan.accounting.wireReceived,
    revenue: totals.revenue + loan.accounting.revenue,
    expenses: totals.expenses + loan.accounting.expenses,
    netProfit: totals.netProfit + loan.accounting.netProfit,
    loansWithPassThrough: totals.loansWithPassThrough + (loan.accounting.hasPassThrough ? 1 : 0),
    auditWarnings: totals.auditWarnings + (loan.accounting.isAuditBalanced ? 0 : 1)
  }), {
    wireReceived: 0,
    revenue: 0,
    expenses: 0,
    netProfit: 0,
    loansWithPassThrough: 0,
    auditWarnings: 0
  });
}

export function buildCompanyRevenueDisplayTotals(loans: RevenueReportLoan[]): CompanyRevenueDisplayTotals {
  return loans.reduce<CompanyRevenueDisplayTotals>((totals, loan) => ({
    wire: totals.wire + loan.accounting.wireReceived,
    loCompensation: totals.loCompensation + loan.loCompensation,
    netWire: totals.netWire + loan.netWire,
    revenue: totals.revenue + loan.accounting.revenue,
    expenses: totals.expenses + loan.accounting.expenses,
    passThrough: totals.passThrough + loan.accounting.passThrough,
    passThroughOutside: totals.passThroughOutside + loan.accounting.passThroughOutside,
    netProfit: totals.netProfit + loan.accounting.netProfit,
    revenueInWire: totals.revenueInWire + loan.accounting.revenueInWire,
    expenseInWire: totals.expenseInWire + loan.accounting.expenseInWire,
    expenseOutside: totals.expenseOutside + (loan.accounting.expenses - loan.accounting.expenseInWire),
    volume: totals.volume + loan.loanAmount,
    units: totals.units + 1
  }), {
    wire: 0,
    loCompensation: 0,
    netWire: 0,
    revenue: 0,
    expenses: 0,
    passThrough: 0,
    passThroughOutside: 0,
    netProfit: 0,
    revenueInWire: 0,
    expenseInWire: 0,
    expenseOutside: 0,
    volume: 0,
    units: 0
  });
}

/**
 * Consolidated recognition view. Loan-level decomposition remains untouched so
 * its wire and P&L audit identities stay valid. Purchase-date servicing interest
 * is removed here and replaced exactly once with receipt-date cash activity.
 */
export function buildUnifiedCompanyRevenueTotals(
  bankedLoans: RevenueReportLoan[],
  brokeredLoans: BrokeredRevenueRecord[],
  servicingEvents: ServicingCashEvent[]
): UnifiedCompanyRevenueTotals {
  const bankedResidualWires = bankedLoans.reduce(
    (sum, loan) => sum + loan.accounting.wireReceived,
    0
  );
  const purchaseBasisServicingInterest = bankedLoans.reduce(
    (sum, loan) => sum + loan.purchaseBasisServicingInterest,
    0
  );
  const bankedContributionBeforeServicing = bankedLoans.reduce(
    (sum, loan) => sum + loan.decomposition.netProfit - loan.purchaseBasisServicingInterest,
    0
  );
  const brokeredGrossChecks = brokeredLoans.reduce(
    (sum, loan) => sum + loan.grossCheckReceived,
    0
  );
  const brokeredCompanyRevenue = brokeredLoans.reduce(
    (sum, loan) => sum + loan.companyRevenue,
    0
  );
  const servicingCashReceived = servicingEvents.reduce((sum, event) => sum + event.total, 0);
  const servicingInterestIncome = servicingEvents.reduce(
    (sum, event) => sum + event.interest + event.lateFee,
    0
  );
  const servicingPrincipalReceived = servicingEvents.reduce(
    (sum, event) => sum + event.principal,
    0
  );
  const servicingEscrowReceived = servicingEvents.reduce(
    (sum, event) => sum + event.escrow,
    0
  );

  return {
    bankedResidualWires,
    bankedContributionBeforeServicing,
    purchaseBasisServicingInterest,
    brokeredGrossChecks,
    brokeredCompanyRevenue,
    servicingCashReceived,
    servicingInterestIncome,
    servicingPrincipalReceived,
    servicingEscrowReceived,
    consolidatedCompanyIncome:
      bankedContributionBeforeServicing + brokeredCompanyRevenue + servicingInterestIncome,
    bankedUnits: bankedLoans.length,
    brokeredUnits: brokeredLoans.length,
    servicingEvents: servicingEvents.length
  };
}

/**
 * Group finalized loans into a contiguous month series (incl. zero-loan months) over [from, to]
 * and aggregate the company-revenue accounting metrics per month, with month-over-month deltas on
 * Net Profit (the Trends focus metric). Pure + side-effect free for unit testing.
 *
 * Mirrors the contiguous-month scaffolding of `groupLoansByMonth` (reports/utils.ts) but for
 * `RevenueReportLoan` keyed on `investorPurchaseDate` instead of `PipelineLoan`/`funded_date`.
 */
export function buildRevenueMonthlyTrends(
  loans: RevenueReportLoan[],
  from: Date,
  to: Date
): RevenueMonthBucket[] {
  const buckets: Array<{ key: string; label: string; loans: RevenueReportLoan[] }> = [];
  let current = startOfMonth(from);
  const end = startOfMonth(to);

  while (!isAfter(current, end)) {
    buckets.push({
      key: format(current, 'yyyy-MM'),
      label: format(current, 'MMM yyyy'),
      loans: []
    });
    current = addMonths(current, 1);
  }

  for (const loan of loans) {
    if (!loan.investorPurchaseDate) {
      continue;
    }
    const monthKey = loan.investorPurchaseDate.slice(0, 7);
    const bucket = buckets.find((entry) => entry.key === monthKey);
    if (bucket) {
      bucket.loans.push(loan);
    }
  }

  const sumNetProfit = (entry: { loans: RevenueReportLoan[] }): number =>
    entry.loans.reduce((sum, loan) => sum + loan.accounting.netProfit, 0);

  return buckets.map((bucket, index) => {
    const revenue = bucket.loans.reduce((sum, loan) => sum + loan.accounting.revenue, 0);
    const expenses = bucket.loans.reduce((sum, loan) => sum + loan.accounting.expenses, 0);
    const netProfit = sumNetProfit(bucket);
    const volume = bucket.loans.reduce((sum, loan) => sum + loan.loanAmount, 0);
    const units = bucket.loans.length;
    const avgLoanSize = units > 0 ? volume / units : 0;
    const avgRevenuePerLoan = units > 0 ? revenue / units : 0;
    const avgNetProfitPerLoan = units > 0 ? netProfit / units : 0;

    let momNetProfitDelta: number | null = null;
    let momNetProfitPct: number | null = null;
    if (index > 0) {
      const previousNetProfit = sumNetProfit(buckets[index - 1]);
      momNetProfitDelta = netProfit - previousNetProfit;
      // abs() denominator keeps the sign correct when the prior month was a loss; null when prior is 0.
      momNetProfitPct = previousNetProfit !== 0
        ? ((netProfit - previousNetProfit) / Math.abs(previousNetProfit)) * 100
        : null;
    }

    return {
      key: bucket.key,
      label: bucket.label,
      revenue,
      expenses,
      netProfit,
      volume,
      units,
      avgLoanSize,
      avgRevenuePerLoan,
      avgNetProfitPerLoan,
      momNetProfitDelta,
      momNetProfitPct
    };
  });
}

export function buildUnifiedRevenueMonthlyTrends(
  bankedLoans: RevenueReportLoan[],
  brokeredLoans: BrokeredRevenueRecord[],
  servicingEvents: ServicingCashEvent[],
  from: Date,
  to: Date
): UnifiedRevenueMonthBucket[] {
  const buckets = new Map<string, UnifiedRevenueMonthBucket>();
  let current = startOfMonth(from);
  const end = startOfMonth(to);

  while (!isAfter(current, end)) {
    const key = format(current, 'yyyy-MM');
    buckets.set(key, {
      key,
      label: format(current, 'MMM yyyy'),
      bankedContribution: 0,
      brokeredCompanyRevenue: 0,
      servicingIncome: 0,
      totalCompanyIncome: 0,
      units: 0
    });
    current = addMonths(current, 1);
  }

  for (const loan of bankedLoans) {
    const bucket = loan.investorPurchaseDate
      ? buckets.get(loan.investorPurchaseDate.slice(0, 7))
      : undefined;
    if (bucket) {
      bucket.bankedContribution += loan.decomposition.netProfit - loan.purchaseBasisServicingInterest;
      bucket.units += 1;
    }
  }

  for (const loan of brokeredLoans) {
    const month = getBusinessDateString(new Date(loan.finalizedAt)).slice(0, 7);
    const bucket = buckets.get(month);
    if (bucket) {
      bucket.brokeredCompanyRevenue += loan.companyRevenue;
      bucket.units += 1;
    }
  }

  for (const event of servicingEvents) {
    const bucket = buckets.get(event.activityDate.slice(0, 7));
    if (bucket) {
      bucket.servicingIncome += event.interest + event.lateFee;
    }
  }

  return [...buckets.values()].map((bucket) => ({
    ...bucket,
    totalCompanyIncome:
      bucket.bankedContribution + bucket.brokeredCompanyRevenue + bucket.servicingIncome
  }));
}

export function buildAuditTotals(loans: RevenueReportLoan[]): AuditTotals {
  return loans.reduce<AuditTotals>((totals, loan) => ({
    revenueInWire: totals.revenueInWire + loan.decomposition.revenueInWire,
    revenueOutside: totals.revenueOutside + loan.decomposition.revenueOutside,
    revenue: totals.revenue + loan.decomposition.totalRevenue,
    expenseInWire: totals.expenseInWire + loan.decomposition.expenseInWire,
    expenseOutside: totals.expenseOutside + loan.decomposition.expenseOutside,
    expenses: totals.expenses + loan.decomposition.totalExpenses,
    passThrough: totals.passThrough + loan.decomposition.passThroughNet,
    passThroughOutside: totals.passThroughOutside + loan.decomposition.passThroughOutside,
    netProfit: totals.netProfit + loan.decomposition.netProfit
  }), {
    revenueInWire: 0,
    revenueOutside: 0,
    revenue: 0,
    expenseInWire: 0,
    expenseOutside: 0,
    expenses: 0,
    passThrough: 0,
    passThroughOutside: 0,
    netProfit: 0
  });
}

/**
 * Suggested balance-sheet clearing account for the Clearing Activity export. A manual pass-through is
 * classified by its clearing type, never by its free-text description.
 */
export function getClearingAccount(item: Pick<WireDecompositionItem, 'description' | 'clearingType'>): string {
  const { description } = item;
  if (item.clearingType === 'manual_pass_through') {
    return 'Clearing / Pass-Through';
  }
  if (description.includes('Shortage')) {
    return 'Warehouse / MOR Shortage Clearing';
  }
  if (description.includes('Escrow')) {
    return 'Escrow / Warehouse Clearing';
  }
  if (description.includes('Haircut')) {
    return 'Non-QM Haircut Reserve Clearing';
  }
  if (description.includes('Government Funding')) {
    return 'Government Funding Fee Clearing';
  }
  if (description.includes('Buydown') || description.includes('Temporary Subsidy')) {
    return 'Temporary Subsidy Buydown Clearing';
  }
  if (description.includes('Reimbursable')) {
    return 'Reimbursable Fees Clearing';
  }
  if (description.includes('Servicing Principal')) {
    return 'Servicing Principal Clearing';
  }
  if (description.includes('FHA UFMIP')) {
    return 'FHA UFMIP / HUD Clearing';
  }
  if (description.includes('(Retained at closing)')) {
    return 'Retained Closing Fees Clearing';
  }
  return 'Clearing / Pass-Through';
}

export function buildClearingActivitySummary(loan: RevenueReportLoan): ClearingActivitySummary {
  const summary: ClearingActivitySummary = {
    shortageFundedGrossMemo: 0,
    shortageFundedWireEffect: 0,
    shortageRecoupedGrossMemo: 0,
    shortageRecoupedWireEffect: 0,
    netShortageGrossMemo: 0,
    netShortageWireEffect: 0,
    escrowWireEffect: 0,
    nonQmHaircutReserveWireEffect: 0,
    governmentFundingGrossMemo: 0,
    governmentFundingWireEffect: 0,
    buydownPassThroughGrossMemo: 0,
    buydownPassThroughWireEffect: 0,
    reimbursableWireEffect: 0,
    servicingPrincipalSettlementWireEffect: 0,
    otherClearingWireEffect: 0,
    fhaUfmipPremiumGrossMemo: 0,
    fhaUfmipPremiumWireEffect: 0,
    fhaUfmipHudRemittance: 0,
    fhaUfmipOutsideClearingEffect: 0,
    totalClearingWireEffect: loan.accounting.passThrough,
    totalOutsideClearingEffect: loan.accounting.passThroughOutside
  };

  for (const item of loan.decomposition.items) {
    if (
      item.bucket !== 'pass_through_in_wire'
      && item.bucket !== 'pass_through_outside'
    ) {
      continue;
    }

    switch (item.clearingType) {
      case 'shortage_funded':
        summary.shortageFundedGrossMemo += item.amount;
        summary.shortageFundedWireEffect += item.wireEffect;
        break;
      case 'shortage_recouped':
        summary.shortageRecoupedGrossMemo += item.amount;
        summary.shortageRecoupedWireEffect += item.wireEffect;
        break;
      case 'escrow':
        summary.escrowWireEffect += item.wireEffect;
        break;
      case 'non_qm_haircut':
        summary.nonQmHaircutReserveWireEffect += item.wireEffect;
        break;
      case 'government_funding':
        summary.governmentFundingGrossMemo += item.amount;
        summary.governmentFundingWireEffect += item.wireEffect;
        break;
      case 'buydown_pass_through':
        summary.buydownPassThroughGrossMemo += item.amount;
        summary.buydownPassThroughWireEffect += item.wireEffect;
        break;
      case 'reimbursable':
        summary.reimbursableWireEffect += item.wireEffect;
        break;
      case 'servicing_principal':
        summary.servicingPrincipalSettlementWireEffect += item.wireEffect;
        break;
      case 'fha_ufmip_premium':
        summary.fhaUfmipPremiumGrossMemo += item.amount;
        summary.fhaUfmipPremiumWireEffect += item.wireEffect;
        break;
      case 'fha_ufmip_hud_remittance':
        summary.fhaUfmipHudRemittance += item.amount;
        summary.fhaUfmipOutsideClearingEffect += item.wireEffect;
        break;
      default:
        summary.otherClearingWireEffect += item.wireEffect;
        break;
    }
  }

  summary.netShortageGrossMemo = summary.shortageFundedGrossMemo - summary.shortageRecoupedGrossMemo;
  summary.netShortageWireEffect = summary.shortageFundedWireEffect + summary.shortageRecoupedWireEffect;
  return summary;
}

export function buildAccountingExportRows(loans: RevenueReportLoan[]): AccountingExportRow[] {
  return loans.map((loan) => {
    const clearing = buildClearingActivitySummary(loan);
    return {
      loanNumber: loan.loanNumber,
      borrowerName: loan.borrowerName,
      loanOfficer: loan.loName,
      investorName: loan.investorName,
      investorPurchaseDate: loan.investorPurchaseDate,
      loanAmount: loan.loanAmount,
      wireReceived: loan.accounting.wireReceived,
      computedWire: loan.decomposition.computedWire,
      wireDifference: loan.accounting.wireReceived - loan.decomposition.computedWire,
      revenue: loan.accounting.revenue,
      expenses: loan.accounting.expenses,
      netProfit: loan.accounting.netProfit,
      clearingWireEffect: clearing.totalClearingWireEffect,
      shortageFundedGrossMemo: clearing.shortageFundedGrossMemo,
      shortageFundedWireEffect: clearing.shortageFundedWireEffect,
      shortageRecoupedGrossMemo: clearing.shortageRecoupedGrossMemo,
      shortageRecoupedWireEffect: clearing.shortageRecoupedWireEffect,
      netShortageGrossMemo: clearing.netShortageGrossMemo,
      netShortageWireEffect: clearing.netShortageWireEffect,
      escrowClearingWireEffect: clearing.escrowWireEffect,
      nonQmHaircutReserveWireEffect: clearing.nonQmHaircutReserveWireEffect,
      governmentFundingGrossMemo: clearing.governmentFundingGrossMemo,
      governmentFundingWireEffect: clearing.governmentFundingWireEffect,
      buydownPassThroughGrossMemo: clearing.buydownPassThroughGrossMemo,
      buydownPassThroughWireEffect: clearing.buydownPassThroughWireEffect,
      reimbursableClearingWireEffect: clearing.reimbursableWireEffect,
      servicingPrincipalSettlementWireEffect: clearing.servicingPrincipalSettlementWireEffect,
      otherClearingWireEffect: clearing.otherClearingWireEffect,
      fhaUfmipPremiumGrossMemo: clearing.fhaUfmipPremiumGrossMemo,
      fhaUfmipPremiumWireEffect: clearing.fhaUfmipPremiumWireEffect,
      fhaUfmipHudRemittance: clearing.fhaUfmipHudRemittance,
      fhaUfmipOutsideClearingEffect: clearing.fhaUfmipOutsideClearingEffect,
      fhaUfmipCompanyVariance: loan.fhaUfmip.companyVarianceAmount,
      balanced: loan.accounting.isAuditBalanced ? 'Yes' : 'Review',
      // Offsetting clearing entries still need balance-sheet treatment.
      suggestedTreatment:
        Math.abs(clearing.totalClearingWireEffect) < 0.005
        && Math.abs(clearing.totalOutsideClearingEffect) < 0.005
        && !hasClearingActivity(loan.decomposition)
        ? 'Book true P&L revenue/expenses only'
        : 'Book clearing activity to balance sheet clearing, not P&L'
    };
  });
}
